import React, { useState, useEffect } from 'react';
import { UserProfile } from '../types';
import { Icons } from './ui/Icons';
import { submitQuestion, getUserQuestions } from '../services/firebase';
import { useAuthModal } from '../contexts/AuthContext';
import type { Question } from '../types';
import { formatAuthorName } from '../utils/formatters';

interface QuestionSegmentProps {
  studyId: string;
  segmentId: string;
  userId: string | null | undefined;
  user: UserProfile | null | undefined;
  prompt: string;
  placeholder: string;
  onSkip?: () => void;
}

export const QuestionSegment: React.FC<QuestionSegmentProps> = ({
  studyId,
  segmentId,
  userId,
  user,
  prompt,
  placeholder,
  onSkip
}) => {
  const { openAuthModal } = useAuthModal();
  const [questionText, setQuestionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userQuestions, setUserQuestions] = useState<Question[]>([]);
  const [showMyQuestions, setShowMyQuestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadUserQuestions = async () => {
      if (!userId) return;
      try {
        const questions = await getUserQuestions(userId, studyId);
        setUserQuestions(questions.filter(q => q.segmentId === segmentId));
      } catch (err) {
        console.error('Failed to load questions:', err);
      }
    };
    loadUserQuestions();
  }, [userId, studyId, segmentId]);

  const handleSubmit = async () => {
    if (!questionText.trim()) {
      setError('Please enter a question');
      return;
    }

    if (!userId || !user) {
      setError('You must be signed in to ask a question');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitQuestion(
        studyId,
        segmentId,
        userId,
        user.email,
        user.displayName,
        questionText
      );

      setSubmitted(true);
      setQuestionText('');

      const questions = await getUserQuestions(userId, studyId);
      setUserQuestions(questions.filter(q => q.segmentId === segmentId));

      setTimeout(() => setSubmitted(false), 3000);
    } catch (err: any) {
      console.error('Failed to submit question:', err);
      setError(err.message || 'Failed to submit question');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Unauthenticated View
  if (!userId) {
    return (
      <div className="space-y-6 text-center py-8 px-4 bg-blue-50/50 rounded-2xl border border-blue-100">
        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm">
          <Icons.MessageSquare className="w-8 h-8 text-primary" />
        </div>
        
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-gray-900">{prompt}</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            Join the conversation! Create a free account to ask questions and discuss this study with the author.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => openAuthModal('signup')}
            className="w-full sm:w-auto px-6 py-3 bg-primary text-white font-bold rounded-xl shadow-md hover:bg-blue-800 transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            <Icons.User className="w-4 h-4" />
            Sign Up to Ask
          </button>
          
          {onSkip && (
            <button
              onClick={onSkip}
              className="w-full sm:w-auto px-6 py-3 bg-white text-gray-600 font-semibold rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              Continue Reading <Icons.Next className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icons.MessageSquare className="w-6 h-6 md:w-8 md:h-8 text-primary" />
        </div>
        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{prompt}</h3>
        <p className="text-gray-500 text-xs md:text-sm">
          Your question will be sent privately to the study author
        </p>
      </div>

      {!submitted ? (
        <div className="bg-white rounded-lg border-2 border-gray-200 p-4">
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder={placeholder}
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none resize-none"
            rows={4}
            disabled={isSubmitting}
          />

          {error && (
            <div className="mt-2 text-sm text-red-600 flex items-center gap-2">
              <Icons.AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() => setShowMyQuestions(!showMyQuestions)}
              className="text-sm text-primary hover:underline"
            >
              {showMyQuestions ? 'Hide' : 'View'} my questions ({userQuestions.length})
            </button>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !questionText.trim() || !userId}
              className="px-6 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Icons.Loader className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Icons.Send className="w-4 h-4" />
                  Submit Question
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center animate-fade-in">
          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <Icons.Check className="w-6 h-6 text-white" />
          </div>
          <h4 className="text-green-900 font-bold mb-2">Question Submitted!</h4>
          <p className="text-green-700 text-sm">
            The study author will review your question and may reply to you.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="mt-4 text-sm text-primary hover:underline"
          >
            Ask another question
          </button>
        </div>
      )}

      {showMyQuestions && userQuestions.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border space-y-3">
          <h4 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
            <Icons.MessageSquare className="w-4 h-4" />
            My Questions
          </h4>
          {userQuestions.map((q) => (
            <div key={q.id} className="bg-white rounded-lg p-3 border">
              <p className="text-sm text-gray-800 mb-2">{q.questionText}</p>
              <p className="text-xs text-gray-500">
                {new Date(q.createdAt).toLocaleDateString()}
              </p>

              {q.replies && q.replies.length > 0 && (
                <div className="mt-3 pl-4 border-l-2 border-blue-500 space-y-2">
                  {q.replies.map((reply) => (
                    <div key={reply.id} className="bg-blue-50 rounded p-2">
                      <p className="text-xs font-semibold text-blue-900 mb-1">
                        {formatAuthorName(reply.authorName)} (Author)
                      </p>
                      <p className="text-sm text-gray-800">{reply.replyText}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
